/* Date helper functions for Magni Sign-in/out Application */

import { EmployeeSignIns, EventData } from "../components/Shared/Types";

/**
 * Checks if two dates represent the same day
 * @param {Date} d1 - First date
 * @param {Date} d2 - Second date
 * @returns {boolean} - True if dates are the same day
 */
function isSameDay(d1:Date, d2:Date) {
  // Check if both dates are valid
  if (!(d1 instanceof Date) || isNaN(d1.getTime()) || 
      !(d2 instanceof Date) || isNaN(d2.getTime())) {
    return false;
  }
  
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Formats a date object to a time string (HH:MM)
 * @param {Date} date - Date to format
 * @returns {string} - Formatted time string
 */
function formatTime(date:Date) {
  // Ensure date is valid before formatting
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid Time';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Formats a date object to a date string
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
function formatDate(date:Date) {
  // Ensure date is valid before formatting
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return date.toLocaleDateString();
}

/**
 * Formats a date object to a long date string with weekday, month, day, and year
 * @param {Date} date - Date to format
 * @returns {string} - Formatted long date string
 */
function formatLongDate(date:Date) {
  // Ensure date is valid before formatting
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return date.toLocaleDateString([], { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}

/**
 * Formats milliseconds to a duration string (Xh Ym)
 * @param {number} ms - Duration in milliseconds
 * @returns {string} - Formatted duration string
 */
function formatDuration(ms:number) {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

/**
 * Checks if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} - True if date is today
 */
function isToday(date:Date) {
  // Ensure date is valid before comparison
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }
  const today = new Date();
  return isSameDay(date, today);
}

/**
 * Gets sign-ins for a specific date from a list of employees
 * @param {Array} employees - List of employees
 * @param {Date} date - Date to filter by
 * @returns {Array} - Filtered list of employees with sign-ins on the specified date
 */
function getSignInsForDate(employees:EmployeeSignIns[], date:Date) {
  // Ensure date is valid before processing
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return [];
  }
  
  const signIns:EmployeeSignIns[] = [];
  employees.forEach(emp => {
    if (!emp.signIns) return; // Skip if no sign-ins

    const entries = emp.signIns.filter(s => {
      // Skip if both dateIn and dateOut are null

      
      const eventDate = s.eventDate;
      return eventDate && isSameDay(new Date(eventDate), date);
    });
    
    if (entries.length) signIns.push({ ...emp, signIns: entries });
  });
  return signIns;
}

/**
 * Gets the count of visits for an employee in a specific month and year
 * @param {Object} employee - Employee object
 * @param {number|null} month - Month to filter by (0-11) or null for all months
 * @param {number|null} year - Year to filter by or null for all years
 * @returns {number} - Count of visits
 */
function getVisitsCount(employee:EmployeeSignIns, month:number, year:number) {
  // If month or year is null, don't filter by that dimension
  return employee.signIns.filter(s => {
    if (s.eventType !== 'in' || s.isMainOffice) return false; // Count sign-ins to visited offices
    
    // Use dateIn if available, otherwise use dateOut
    const eventDate = s.eventDate;
    if (!eventDate) return false; // Skip if no valid date
    
    if (month != null && eventDate.getMonth() !== month) return false;
    if (year != null && eventDate.getFullYear() !== year) return false;
    return true;
  }).length;
}

/**
 * Gets the total duration for an employee in a specific month and year
 * Using the improved implementation from date-helpers-new.js
 * @param {Object} employee - Employee object
 * @param {number|null} month - Month to filter by (0-11) or null for all months
 * @param {number|null} year - Year to filter by or null for all years
 * @returns {number} - Total duration in milliseconds
 */
function getTotalDuration(employee:EmployeeSignIns, month:number, year:number) {
  // Calculate total duration from completed trips
  let totalDuration = 0;
  
  // Process sign-ins/outs to calculate durations
  const signIns = [...employee.signIns]
    .filter(s => {
      const eventDate = s.eventDate;
      if (!eventDate) return false; // Skip if no valid date
      
      return true; // Keep all events, we'll filter by month/year in the loop
    })
    .sort((a, b) => {
      return a.eventDate.getTime() - b.eventDate.getTime();
    });
  
  // Group events into trips
  let currentTrip = null;
  
  for (let i = 0; i < signIns.length; i++) {
    const event = signIns[i];
    const eventDate = event.eventDate;
    
    // Skip if no valid date (shouldn't happen due to filter above)
    if (!eventDate) continue;
    
    // Filter by month/year if specified
    if (month != null && eventDate.getMonth() !== month) continue;
    if (year != null && eventDate.getFullYear() !== year) continue;
    
    if (event.eventType === 'out' && event.isMainOffice) {
      // Start of a trip
      currentTrip = { startTime: eventDate };
    } else if (currentTrip && event.eventType === 'out' && !event.isMainOffice) {
      // End of a trip (left visited location)
      if (currentTrip.startTime) {
        totalDuration += (eventDate.getTime() - currentTrip.startTime.getTime());
      }
      currentTrip = null;
    }
  }
  
  return totalDuration;
}

/**
 * Gets profile statistics for a Magni employee
 * @param {Object} user - User object
 * @param {string} dateFilter - Filter type ('week', 'month', 'year', 'all')
 * @param {number|null} customMonth - Custom month to filter by (0-11)
 * @param {number|null} customYear - Custom year to filter by
 * @returns {Object} - Profile statistics
 */
function getMagniProfileStats(user:EmployeeSignIns, dateFilter:string, customMonth:number, customYear:number) {
  // dateFilter can be 'week', 'month', 'year', 'all'
  // customMonth / customYear are numeric or null
  const now = new Date();
  const events = user.signIns.filter(e => {
    // Skip if no valid date

    
    // Use dateIn if available, otherwise use dateOut
    const eventDate = e.eventDate;
    
    // handle dateFilter
    if (dateFilter !== 'all') {
      if (dateFilter === 'year') {
        if (eventDate.getFullYear() !== now.getFullYear()) return false;
      } else if (dateFilter === 'month') {
        if (eventDate.getMonth() !== now.getMonth() || eventDate.getFullYear() !== now.getFullYear()) return false;
      } else if (dateFilter === 'week') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        if (eventDate < startOfWeek || eventDate > endOfWeek) return false;
      }
    }
    // handle customMonth/customYear
    if (customMonth != null && eventDate.getMonth() !== customMonth) return false;
    if (customYear != null && eventDate.getFullYear() !== customYear) return false;
    return true;
  }).sort((a, b) => {
    return a.eventDate.getTime() - b.eventDate.getTime();
  });

  // Count visits to locations (based on sign-ins to visited offices)
  const locationCounts:any = {};
  events.forEach((e:EventData) => {
    if (e.eventType === 'in' && !e.isMainOffice && e.location) {
      locationCounts[e.location] = (locationCounts[e.location] || 0) + 1;
    }
  });
  let topLocations = Object.entries(locationCounts).map(([name, visits]) => ({ name, visits }));
  topLocations.sort((a:any, b:any) => b.visits - a.visits);
  topLocations = topLocations.slice(0, 5);

  // Group events into trips - modified to track from out at main to out at visited
  const timeOutOfOffice:any[] = [];
  let currentTrip:any = null;
  
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const eventDate = event.eventDate;
    
    // Skip if eventDate is somehow still null (shouldn't happen due to filter)
    if (!eventDate) continue;
    
    if (event.eventType === 'out' && event.isMainOffice) {
      // Start of a new trip
      currentTrip = {
        date: eventDate,
        startTime: eventDate,
        fromLocation: event.location,
        events: [event],
        duration: 0,
      };
    } else if (currentTrip && event.eventType === 'in' && !event.isMainOffice) {
      // Arrived at visited location
      currentTrip.toLocation = event.location;
      currentTrip.events.push(event);
    } else if (currentTrip && event.eventType === 'out' && !event.isMainOffice) {
      // Left visited location - complete trip
      currentTrip.endTime = eventDate;
      currentTrip.duration = eventDate.getTime() - currentTrip.startTime.getTime();
      currentTrip.events.push(event);
      
      if (currentTrip.toLocation) { // Only add complete trips
        timeOutOfOffice.push(currentTrip);
      }
      currentTrip = null;
    }
  }
  
  // Add current incomplete trip if it exists
  if (currentTrip && currentTrip.toLocation) {
    currentTrip.endTime = null; // Still ongoing
    currentTrip.duration = now.getTime() - currentTrip.startTime.getTime();
    timeOutOfOffice.push(currentTrip);
  }
  
  return { topLocations, timeOutOfOffice };
}

// Export all functions
export {
  isSameDay,
  formatTime,
  formatDate,
  formatLongDate,
  formatDuration,
  isToday,
  getSignInsForDate,
  getVisitsCount,
  getTotalDuration,
  getMagniProfileStats
};